import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextareaInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  BILLING_REOPEN_TARGET_LABELS,
  listDokumentBillingReopenLog,
  listHeroBillingReopenLog,
  markDokumentBillingReopen,
  markHeroBillingReopen,
  RECHNUNG_ART_LABELS,
  type BillingReopenLogEntry,
  type BillingReopenTarget,
  type RechnungArt,
} from "@/lib/dokumente";
import { DOCUMENT_EDITOR_OVERLAY_Z } from "@/pages/dokumente/documentEditorLayers";

export type BillingReopenSource = "gd" | "hero";

export interface BillingReopenSubject {
  source: BillingReopenSource;
  id: number;
  label: string;
  auftragId?: number | null;
  auftragNummer?: string | null;
  currentArt?: string | null;
}

interface BillingReopenDialogProps {
  open: boolean;
  subject: BillingReopenSubject | null;
  onClose: () => void;
  onDone: () => void;
  zIndexClass?: string;
}

function artLabel(art: string | null | undefined): string {
  if (!art) return "Rechnung";
  if (art === "rechnung") return "Rechnung";
  return RECHNUNG_ART_LABELS[art as RechnungArt] || art;
}

export function BillingReopenDialog({
  open,
  subject,
  onClose,
  onDone,
  zIndexClass = DOCUMENT_EDITOR_OVERLAY_Z,
}: BillingReopenDialogProps) {
  const [target, setTarget] = useState<BillingReopenTarget>("teilrechnung");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [logEntries, setLogEntries] = useState<BillingReopenLogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(false);

  const currentArt = subject?.currentArt ?? null;

  useEffect(() => {
    if (!open || !subject) return;
    setTarget(currentArt === "teilrechnung" ? "vorkasse" : "teilrechnung");
    setReason("");
    setLogLoading(true);
    const loader =
      subject.source === "hero"
        ? listHeroBillingReopenLog(subject.id, 10)
        : listDokumentBillingReopenLog(subject.id, 10);
    void loader
      .then(setLogEntries)
      .catch(() => setLogEntries([]))
      .finally(() => setLogLoading(false));
  }, [open, subject, currentArt]);

  const canSubmit =
    Boolean(subject) &&
    reason.trim().length >= 3 &&
    !saving &&
    target !== currentArt;

  async function handleSubmit() {
    if (!canSubmit || !subject) return;
    setSaving(true);
    try {
      if (subject.source === "hero") {
        await markHeroBillingReopen(subject.id, {
          target,
          reason: reason.trim(),
          auftrag_id: subject.auftragId ?? undefined,
        });
      } else {
        await markDokumentBillingReopen(subject.id, {
          target,
          reason: reason.trim(),
        });
      }
      toast.success(
        `Als ${BILLING_REOPEN_TARGET_LABELS[target]} markiert – Fertigstellung aufgehoben.`,
      );
      onDone();
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Ummarkierung fehlgeschlagen.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open && subject != null}
      onClose={saving ? () => undefined : onClose}
      title="Rechnung ummarkieren"
      zIndexClass={zIndexClass}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={saving}
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            Speichern
          </Button>
        </>
      }
    >
      {subject ? (
        <div className="space-y-4 text-sm">
          <p className="text-label-secondary">
            Die Rechnung wird als Teilrechnung oder Vorkasse-Rechnung markiert. Dadurch wird die
            Fertigstellung des Projekts aufgehoben, damit weitergearbeitet werden kann. Die
            Begründung wird im Log gespeichert
            {subject.source === "hero" ? " (lokale App-Markierung, ohne Änderung in Hero)." : "."}
          </p>

          <div className="rounded-ios border border-separator bg-card-elevated p-3">
            <div className="font-semibold text-label">
              {subject.label} · aktuell {artLabel(currentArt)}
            </div>
            {subject.auftragNummer ? (
              <div className="mt-1 text-label-secondary">Projekt {subject.auftragNummer}</div>
            ) : null}
            {subject.source === "hero" ? (
              <div className="mt-1 text-xs text-label-tertiary">Hero-Rechnung</div>
            ) : null}
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-label">Neue Markierung</legend>
            {(Object.keys(BILLING_REOPEN_TARGET_LABELS) as BillingReopenTarget[]).map((value) => (
              <label key={value} className="flex items-start gap-2">
                <input
                  type="radio"
                  name="billing-reopen-target"
                  className="mt-0.5"
                  checked={target === value}
                  disabled={currentArt === value || saving}
                  onChange={() => setTarget(value)}
                />
                <span>
                  <span className="font-medium text-label">
                    {BILLING_REOPEN_TARGET_LABELS[value]}
                  </span>
                  {value === "vorkasse" ? (
                    <span className="block text-xs text-label-tertiary">
                      Setzt zusätzlich die Projekt-Zahlungsart auf Vorkasse.
                    </span>
                  ) : (
                    <span className="block text-xs text-label-tertiary">
                      Projekt gilt als teilabgerechnet, Fertigstellung wird aufgehoben.
                    </span>
                  )}
                </span>
              </label>
            ))}
          </fieldset>

          <TextareaInput
            label="Begründung (Pflicht)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Warum wird die Abrechnung zurückgestuft?"
            rows={3}
            required
          />

          <div className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
              Bisherige Ummarkierungen
            </h4>
            {logLoading ? (
              <p className="text-xs text-label-secondary">Log wird geladen …</p>
            ) : logEntries.length === 0 ? (
              <p className="text-xs text-label-secondary">Noch keine Einträge.</p>
            ) : (
              <ul className="max-h-40 space-y-2 overflow-y-auto text-xs">
                {logEntries.map((entry) => (
                  <li
                    key={entry.id}
                    className="rounded-ios border border-separator bg-card-elevated px-3 py-2"
                  >
                    <div className="font-medium text-label">
                      {artLabel(entry.previous_rechnung_art)} →{" "}
                      {artLabel(entry.target_rechnung_art)}
                    </div>
                    <div className="text-label-secondary">
                      {entry.created_by_display || entry.created_by_login}
                      {" · "}
                      {entry.created_at
                        ? new Date(entry.created_at).toLocaleString("de-DE")
                        : "—"}
                    </div>
                    <div className="mt-1 text-label">{entry.reason}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
